import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Bell, BellRinging, TrendUp, TrendDown, Trophy, Clock, GraduationCap,
  Fire, Wallet, Eye, Coins, Sparkle, X, Check,
} from '@phosphor-icons/react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const ICONS = {
  crash: TrendDown, drawdown: TrendDown, surge: TrendUp, milestone: Trophy,
  watch: Eye, inactive: Clock, learn: GraduationCap, streak: Fire,
  bet: Coins, idlecash: Wallet, welcome: Sparkle,
};

const TONES = {
  danger: 'text-red-400 bg-red-500/10 border-red-500/20',
  success: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  gold: 'text-gold bg-gold/10 border-gold/25',
  info: 'text-cyan-300 bg-cyan-500/10 border-cyan-500/20',
};

/* One shared feed for both bell instances (desktop sidebar + mobile top bar):
   only one polls, every mutation is broadcast so no badge ever goes stale. */
const store = { items: [], unread: 0 };
const listeners = new Set();
let seenIds = null;
let pollOwner = null;

const publish = (items, unread) => {
  store.items = items;
  store.unread = unread;
  listeners.forEach((l) => l(items, unread));
};

const ago = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const NotificationBell = ({ align = 'left' }) => {
  const { user } = useAuth();
  const [items, setItems] = useState(store.items);
  const [unread, setUnread] = useState(store.unread);
  const [open, setOpen] = useState(false);
  const selfId = useRef({});
  const navigate = useNavigate();

  useEffect(() => {
    const listener = (nextItems, nextUnread) => { setItems(nextItems); setUnread(nextUnread); };
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      const list = data.notifications || [];
      if (seenIds) {
        list
          .filter((n) => !n.read && !seenIds.has(n.id))
          .slice(0, 2)
          .forEach((n) => toast(n.title, { description: n.body, duration: 6000 }));
      }
      seenIds = new Set(list.map((n) => n.id));
      publish(list, data.unread_count || 0);
    } catch (_) { /* silent — alerts are non-critical */ }
  }, []);

  useEffect(() => {
    if (!user || user.is_guest) return;
    const me = selfId.current;
    if (pollOwner === null) pollOwner = me;
    if (pollOwner !== me) return;
    load();
    const iv = setInterval(load, 60000);
    return () => { clearInterval(iv); if (pollOwner === me) pollOwner = null; };
  }, [user, load]);

  if (!user || user.is_guest) return null;

  const openItem = (n) => {
    if (!n.read) {
      publish(store.items.map((i) => (i.id === n.id ? { ...i, read: true } : i)), Math.max(0, store.unread - 1));
      api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    if (n.link) { setOpen(false); navigate(n.link); }
  };

  const markAll = () => {
    publish(store.items.map((i) => ({ ...i, read: true })), 0);
    api.post('/notifications/read-all').catch(() => {});
  };

  const dismiss = (e, id) => {
    e.stopPropagation();
    const wasUnread = store.items.some((i) => i.id === id && !i.read);
    publish(store.items.filter((i) => i.id !== id), wasUnread ? Math.max(0, store.unread - 1) : store.unread);
    api.delete(`/notifications/${id}`).catch(() => {});
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`relative z-50 w-9 h-9 flex items-center justify-center rounded-xl border transition-colors ${
          unread > 0
            ? 'text-gold border-gold/30 bg-gold/10 hover:bg-gold/15'
            : 'text-zinc-400 border-white/10 bg-white/5 hover:text-white'
        }`}
        aria-label="Notifications"
        data-testid="notification-bell"
      >
        {unread > 0 ? <BellRinging size={18} weight="fill" /> : <Bell size={18} />}
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-testid="notification-backdrop" />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.97 }}
              transition={{ duration: 0.16 }}
              className={`absolute top-11 z-50 w-[320px] max-w-[calc(100vw-2rem)] rounded-2xl bg-zinc-950 border border-white/10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.9)] overflow-hidden ${
                align === 'right' ? 'right-0' : 'left-0'
              }`}
              data-testid="notification-panel"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Alerts</p>
                {unread > 0 && (
                  <button
                    onClick={markAll}
                    className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1"
                    data-testid="mark-all-read-btn"
                  >
                    <Check size={11} weight="bold" /> Mark all read
                  </button>
                )}
              </div>

              <div className="max-h-[380px] overflow-y-auto">
                {items.length === 0 ? (
                  <div className="px-4 py-10 text-center" data-testid="notifications-empty">
                    <Bell size={26} className="text-zinc-700 mx-auto mb-2" />
                    <p className="text-xs text-zinc-500">No alerts yet.</p>
                    <p className="text-[10px] text-zinc-600 mt-1">Price swings and streak reminders land here.</p>
                  </div>
                ) : (
                  items.map((n) => {
                    const Icon = ICONS[n.type] || Bell;
                    const tone = TONES[n.severity] || TONES.info;
                    return (
                      <div
                        key={n.id}
                        onClick={() => openItem(n)}
                        className={`group flex gap-3 px-4 py-3 border-b border-white/[0.04] cursor-pointer transition-colors hover:bg-white/[0.03] ${
                          n.read ? 'opacity-60' : ''
                        }`}
                        data-testid={`notification-item-${n.id}`}
                      >
                        <span className={`w-8 h-8 flex-shrink-0 rounded-xl border flex items-center justify-center ${tone}`}>
                          <Icon size={15} weight="fill" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] text-white font-semibold leading-snug">{n.title}</p>
                          <p className="text-[11px] text-zinc-400 leading-snug mt-0.5">{n.body}</p>
                          <p className="text-[9px] text-zinc-600 mt-1">{ago(n.created_at)}</p>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-gold mt-1.5" />}
                          <button
                            onClick={(e) => dismiss(e, n.id)}
                            className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition-opacity"
                            aria-label="Dismiss"
                            data-testid={`notification-dismiss-${n.id}`}
                          >
                            <X size={11} weight="bold" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
